import constants from '@vue3/constants'
const { DARK, SYSTEM } = constants.theme

// Helper function for system theme detection
function getSystemTheme () {
  const matchMedia = window.matchMedia?.('(prefers-color-scheme: dark)')
  return matchMedia ? matchMedia.matches : false
}


// Initialize AndroidJSClient early to handle any delays
if (typeof window !== 'undefined') {
  // Set up persistent theme listener that's always available
  window.onFilesSelected = (filesJsonString) => {
    if (window.activeAndroidUploader && typeof window.activeAndroidUploader.handleAndroidFilesSelected === 'function') {
      window.activeAndroidUploader.handleAndroidFilesSelected(filesJsonString)
    } else {
      console.warn('No active Android uploader found to process selected files.')
    }
  }
}

const {
  IST,
  IST_OFFSET,
  WIB,
  WIB_OFFSET,
  WITA,
  WITA_OFFSET,
  WIT,
  WIT_OFFSET,
  UTC,
  UTC_OFFSET
} = constants.timeZone

export const getLocale = () => {
  if (!window.localStorage.getItem('OMG_APPLE_TRANSLATE_LANG_KEY')) {
    window.localStorage.setItem('OMG_APPLE_TRANSLATE_LANG_KEY', 'ID')
  }
  return window.localStorage.getItem('OMG_APPLE_TRANSLATE_LANG_KEY') || 'ID'
}

export const setLocale = (locale) => {
  window.localStorage.setItem('OMG_APPLE_TRANSLATE_LANG_KEY', locale)
  window.location.reload()
}

export function setAppConfiguration (typeOfConfiguration, config) {
  window.localStorage.setItem(typeOfConfiguration, config)
}

export function getAppConfiguration (typeOfConfiguration) {
  return window.localStorage.getItem(typeOfConfiguration)
}

export function applyTheme (theme) {

  if (theme === SYSTEM && isAndroidAppCheck) {
    // For Android SYSTEM theme, request from Android
    requestSystemThemeFromAndroid()
    return
  }

  const isDark = theme === DARK || (theme === SYSTEM && getSystemTheme())

  if (isDark) {
    document.documentElement.setAttribute('dark', '')
  } else {
    document.documentElement.removeAttribute('dark')
  }
  postEventMessageToIOS('iosListener', theme)
}

export { getSystemTheme }

function requestSystemThemeFromAndroid () {
  // Request system theme from Android
  // The listener is already set up at module initialization
  postEventMessageToAndroid({
    theme: 'SYSTEM'
  })
  window.themeFetchedFromSystem = (themeResponse) => {
    try {
      const theme = typeof themeResponse === 'string' ? themeResponse : themeResponse?.theme
      if (theme === 'LIGHT' || theme === 'DARK') {
        // Apply the theme returned by Android
        if (theme === 'DARK') {
          document.documentElement.setAttribute('dark', '')
        } else {
          document.documentElement.removeAttribute('dark')
        }
      } else {
        // Fallback to web system theme detection
        const isDark = getSystemTheme()
        if (isDark) {
          document.documentElement.setAttribute('dark', '')
        } else {
          document.documentElement.removeAttribute('dark')
        }
      }
    } catch (error) {
      // Fallback to web system theme detection
      const isDark = getSystemTheme()
      if (isDark) {
        document.documentElement.setAttribute('dark', '')
      } else {
        document.documentElement.removeAttribute('dark')
      }
    }
  }
}

const userAgent = navigator.userAgent
export const isIOSAppCheck = userAgent.includes('AppleReseller_iOS')
export const isIOSVersionBeforeBlue = isIOSAppCheck && userAgent.includes('AppleReseller_iOS-1.0')
const parentChildunSupportedVersions = ['AppleReseller_iOS-1.0', 'AppleReseller_iOS-1.2']
export const isIOSAppParentChildUnsupportedVersion = parentChildunSupportedVersions.some(version => userAgent.includes(version))

export const isAndroidAppCheck = userAgent.includes('Android')

export function postEventMessageToIOS (eventName, data) {
  window.webkit?.messageHandlers?.[eventName]?.postMessage(data)
}

export function postEventMessageToAndroid (data, callback) {
  // Set up callback handler if provided (Android will call this back)
  if (callback && typeof callback === 'function') {
    window.handleData = (responseData) => {
      try {
        const parsedData = typeof responseData === 'string' ? JSON.parse(responseData) : responseData
        callback(parsedData)
      } catch (error) {
        console.error('Error parsing Android callback data:', error)
        callback(null, error)
      }
      // Clean up the global handler after use
      delete window.handleData
    }
  }

  // Always call AndroidJSClient.handleData method
  window.AndroidJSClient?.handleData?.(JSON.stringify(data))
}

export const shouldMakeSessionUpdateCall = () => {
  let lastSessionUpdateDate = window.localStorage.getItem(constants.SESSION_UPDATED_DATE)
  if (!lastSessionUpdateDate) {
    return true
  }
  const currentDate = new Date()
  currentDate.setHours(0, 0, 0, 0)
  return currentDate.getTime() > lastSessionUpdateDate
}

export const setSessionUpdatedDate = () => {
  const currentDate = new Date()
  currentDate.setHours(0, 0, 0, 0)
  window.localStorage.setItem(constants.SESSION_UPDATED_DATE, currentDate.getTime())
}

export const clearSessionUpdatedDate = () => {
  window.localStorage.removeItem(constants.SESSION_UPDATED_DATE)
}

export function getCondensedBannersConfigResponse (response) {
  return response?.map(component => {
    const bannerProperties = component.parameters[0]
    return {
      parameters: [
        {
          id: bannerProperties.id,
          image: bannerProperties.image,
          url: bannerProperties.url
        }
      ]
    }
  })
}

export function setLocalStorageAppConfiguration (typeOfConfiguration, config) {
  window.localStorage.setItem(typeOfConfiguration, config)
}

export function getLocalStorageAppConfiguration (typeOfConfiguration) {
  return window.localStorage.getItem(typeOfConfiguration)
}

export const emitEventToVuexStore = (eventType, data) => {
  window.dispatchEvent(new CustomEvent('update.vuex.state.from.pinia',
    { detail: { eventType, data } }))
}

export const setRejectedReasonViewed = (tenantId = '') => {
  const key = `${constants.IS_REJECTED_REASON_VIEWED}_${tenantId}`
  window.localStorage.setItem(key, true)
}

export const removeRejectedReasonViewed = (tenantId = '') => {
  const key = `${constants.IS_REJECTED_REASON_VIEWED}_${tenantId}`
  window.localStorage.removeItem(key)
}

export const getRejectedReasonViewed = (tenantId = '') => {
  const key = `${constants.IS_REJECTED_REASON_VIEWED}_${tenantId}`
  return window.localStorage.getItem(key)
}

export function setLocalStorageItem (key, value) {
  window.localStorage.setItem(key, value)
}

export function getLocalStorageItem (key) {
  return window.localStorage.getItem(key)
}

export function removeLocalStorageItem (key) {
  return window.localStorage.removeItem(key)
}

export const getTimeZone = () => {
  const offset = new Date().getTimezoneOffset()
  switch (offset) {
    case IST_OFFSET:
      return IST
    case WIB_OFFSET:
      return WIB
    case WITA_OFFSET:
      return WITA
    case WIT_OFFSET:
      return WIT
    case UTC_OFFSET:
      return UTC
    default:
      return ''
  }
}


export const convertBase64ToBlob = (base64FileContent) => {
  const base64XlsxData = base64FileContent
  const mimeType = 'application/octet-stream'
  const binaryString = atob(base64XlsxData)
  const byteArray = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    byteArray[i] = binaryString.charCodeAt(i)
  }
  return new Blob([byteArray], { type: mimeType })
}

export const bindClicks = {
  mounted (el, binding) {
    const clickHandler = (event) => {
      const target = event?.target
      if (target?.tagName === 'A' && target?.dataset) {
        event.preventDefault()
        const { link, isdocument: isDocument, openinnewtab: openInNewTab } = target.dataset || {}
        binding.instance?.viewTickerURL?.(link, isDocument === 'true', openInNewTab)
      }
    }
    el._clickHandler = clickHandler
    el.addEventListener('click', clickHandler)
  },
  unmounted (el) {
    el.removeEventListener('click', el._clickHandler)
    delete el._clickHandler
  }
}

export function getThreeMonthNames (startDate, locale = 'id-ID', options = { month: 'long' }) {
  const monthNameList = []
  if (!startDate) {
    return monthNameList
  }
  const date = new Date(startDate)
  date?.setDate(1)
  for (let i = 0; i < 3; i++) {
    monthNameList.push(date.toLocaleString(locale, options))
    date.setMonth(date.getMonth() + 1)
  }
  return monthNameList
}

export function isAndroidBridgeAvailable () {
  return !!(window.AndroidJSClient && typeof window.AndroidJSClient.handleData === 'function')
}

export function base64ToBlob (base64Data, contentType) {
  const sliceSize = 512
  const byteCharacters = atob(base64Data)
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)
    const byteNumbers = new Array(slice.length)

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  return new Blob(byteArrays, { type: contentType })
}

export function triggerNativeFilePicker (uploader) {
  window.activeAndroidUploader = uploader
  const payload = {
    selectFiles: {
      mimeType: '*/*'
    }
  }
  window.AndroidJSClient?.handleData?.(JSON.stringify(payload))
}

export function handleUploaderClickHelper (e, uploader, fileFormatCaption, allowedExtensions) {
  if (isAndroidAppCheck && isAndroidBridgeAvailable()) {
    e.preventDefault()
    e.stopPropagation()
    if (uploader) {
      uploader.androidUploadContext = {
        fileFormatCaption,
        allowedExtensions
      }
    }
    triggerNativeFilePicker(uploader)
    return true
  }
  return false
}

